import { AppBar } from "@/elements/components/ui/app-bar";
import { Tab } from "@/elements/components/ui/tab";
import { api } from "@/elements/components/utils/convex-api";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import React, { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ScrollView, Text, View } from "react-native";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Select } from "@/elements/components/ui/select";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import { useSheet } from "@/elements/components/shared/sheet-provider";
import { Button } from "@/elements/components/ui/button";
import { Textarea } from "@/elements/components/ui/textarea";
import { Id } from "@teachloop/backend/_generated/dataModel";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import {
  SingleSelectSheetPayload,
  SingleSelectSheetResult,
} from "@/elements/components/sheets/single-select-sheet";
import goBack from "@/elements/components/utils/go-back";

const safeParse = (value: string) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error(error);
    return null;
  }
};

const ChildQualificationSchema = z.object({
  subjects: z.array(
    z.object({
      subjectId: z.string().min(1),
      qualificationId: z.string().min(1),
      examDetails: z.string().optional(),
    })
  ),
});

type ChildQualificationSchemaType = z.infer<typeof ChildQualificationSchema>;

function ChildQualificationScreen() {
  const router = useRouter();
  const { child } = useLocalSearchParams();
  const childData = safeParse(child as string);
  const yearGroup = childData?.yearGroupId;
  const subjects: any = childData?.subjects ?? [];
  const {
    control,
    watch,
    setValue,
    formState: { isValid: isFormValid },
    handleSubmit,
  } = useForm<ChildQualificationSchemaType>({
    resolver: zodResolver(ChildQualificationSchema),
    mode: "onChange",
    defaultValues: {
      subjects: subjects,
    },
  });
  const [activeSubjectId, setActiveSubjectId] = useState(
    watch("subjects")?.[0]?.subjectId
  );
  const activeSubjectIndex = watch("subjects").findIndex(
    (subject) => subject.subjectId === activeSubjectId
  );
  const { open: openSingleSelectSheet } = useSheet<
    SingleSelectSheetPayload,
    SingleSelectSheetResult
  >(SheetIds.SINGLE_SELECT);

  const { data: parentProfile } = useQuery(
    convexQuery(api.parent.onboarding.queries.getProfile, {})
  );
  const parentProfileId = parentProfile?._id;

  const { data: subjectsForYearGroup = [] } = useQuery({
    ...convexQuery(api.parent.onboarding.queries.getSubjectsForYearGroup, {
      yearGroupId: yearGroup as Id<"yearGroups">,
    }),
    enabled: !!yearGroup,
  });

  const subjectsToDisplay: typeof subjectsForYearGroup = subjects.map(
    (subjectData: { subjectId: string }) =>
      subjectsForYearGroup.find(
        (subject) => subject.value === subjectData.subjectId
      )
  );

  const { data: qualificationTypes = [] } = useQuery({
    ...convexQuery(
      api.parent.onboarding.queries.getAvailableQualificationsForYearGroup,
      {
        yearGroupId: yearGroup as Id<"yearGroups">,
      }
    ),
    enabled: !!yearGroup,
  });

  const handleQualificationOptionsOpen = useCallback(async () => {
    const result = await openSingleSelectSheet({
      options: qualificationTypes,
      preSelectedOption:
        watch("subjects")?.[activeSubjectIndex]?.qualificationId,
    });
    if (result?.selectedOption) {
      setValue(
        `subjects.${activeSubjectIndex}.qualificationId`,
        result.selectedOption as string,
        {
          shouldValidate: true,
        }
      );
    }
  }, [
    openSingleSelectSheet,
    qualificationTypes,
    watch,
    setValue,
    activeSubjectIndex,
  ]);

  const { mutateAsync: createChild } = useMutation({
    mutationFn: useConvexMutation(api.parent.onboarding.mutations.createChild),
  });

  const { mutateAsync: updateChild } = useMutation({
    mutationFn: useConvexMutation(api.parent.onboarding.mutations.updateChild),
  });

  const { mutateAsync: createProfileByAddingStudent } = useMutation({
    mutationFn: useConvexMutation(
      api.parent.onboarding.mutations.createProfileByAddingStudent
    ),
  });

  const handleNext = useCallback(
    async (data: ChildQualificationSchemaType) => {
      if (childData?.studentId) {
        await updateChild({
          ...childData,
          subjects: data.subjects,
          learningPreferences: ["in_person", "remote"],
        });
      } else {
        if (parentProfileId) {
          await createChild({
            ...childData,
            subjects: data.subjects,
            learningPreferences: ["in_person", "remote"],
          });
        } else {
          await createProfileByAddingStudent({
            child: {
              ...childData,
              subjects: data.subjects,
              learningPreferences: ["in_person", "remote"],
            },
            onboardingStepCompleted: "childDetails",
          });
        }
      }
      router.push("/(app)/onboarding/(parent)/manage-children");
    },
    [
      childData,
      createChild,
      router,
      parentProfileId,
      updateChild,
      createProfileByAddingStudent,
    ]
  );

  if (!child) {
    return <Redirect href="/(app)/onboarding/(parent)/add-child" />;
  }

  return (
    <View className="flex-1 bg-teachLoop-creamLight">
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-4" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
                Tell us about your child
              </Text>
              <Text className="font-geologica-regular font-normal text-xl text-black leading-[1.5]">
                What is your child studying now?
              </Text>
            </View>

            <View className="h-8" />

            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerClassName="flex-row gap-2"
            >
              {subjectsToDisplay.map((subject) => (
                <Tab
                  size="md"
                  key={subject?.value}
                  intent={
                    subject?.value === activeSubjectId ? "active" : "inActive"
                  }
                  onPress={() => {
                    setActiveSubjectId(subject?.value);
                  }}
                >
                  <Tab.Text size="md">{subject?.label}</Tab.Text>
                </Tab>
              ))}
            </ScrollView>

            <View className="h-8" />

            <View className="gap-6" key={activeSubjectId}>
              <Controller
                control={control}
                name={`subjects.${activeSubjectIndex}.qualificationId`}
                render={({ field }) => (
                  <Select onPress={handleQualificationOptionsOpen}>
                    <Select.Label>
                      {field.value
                        ? (qualificationTypes ?? []).find(
                            (qualificationType) =>
                              qualificationType.value === field.value
                          )?.label
                        : "Qualification"}
                    </Select.Label>
                    <Select.DownArrow />
                  </Select>
                )}
              />
              <View className="gap-4">
                <Text className="font-geologica-medium leading-[1.5] font-medium text-base text-black">
                  Exam board details
                </Text>
                <Controller
                  control={control}
                  name={`subjects.${activeSubjectIndex}.examDetails`}
                  render={({ field }) => (
                    <Textarea
                      multiline
                      placeholder="e.g- AQA, Edexcel, CIE etc. 
If unsure, your child may know or you can usually fine this on the school website or subject handbook. If you cannot find this, please do not worry as this can be filled in later."
                      size="md"
                      {...field}
                    />
                  )}
                />
              </View>
            </View>
            <View className="h-8" />
            <Button
              disabled={!isFormValid}
              fullWidth
              size="md"
              onPress={handleSubmit(handleNext)}
            >
              <Button.Text size="md">Next</Button.Text>
            </Button>
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default ChildQualificationScreen;
